import * as React from "react";

/**
 * Scan ricorsivo (depth-first) dei children React per individuare la presenza
 * di un componente Description (Radix Dialog/AlertDialog/Sheet).
 *
 * Match euristico sul `displayName`/`name` del componente: qualsiasi nome che
 * termina con "Description" è considerato valido. Copre:
 *   - DialogPrimitive.Description / DialogDescription
 *   - AlertDialogPrimitive.Description / AlertDialogDescription
 *   - SheetDescription (alias di DialogPrimitive.Description)
 *
 * Lo scan attraversa fragment, array e React.element children. Ferma alla
 * prima occorrenza per minimizzare il costo. NON entra dentro componenti
 * black-box (functional components con children dinamici): per quei pochi
 * casi il consumer può passare `aria-describedby` esplicitamente.
 */
export function hasDescriptionChild(node: React.ReactNode): boolean {
  if (node == null || typeof node === "boolean") return false;
  if (Array.isArray(node)) return node.some(hasDescriptionChild);
  if (!React.isValidElement(node)) return false;
  const type = node.type as { displayName?: string; name?: string } | string;
  if (typeof type !== "string") {
    const name = type.displayName || type.name || "";
    if (name.endsWith("Description")) return true;
  }
  const props = node.props as { children?: React.ReactNode } | undefined;
  return props?.children ? hasDescriptionChild(props.children) : false;
}

/**
 * Restituisce `{ "aria-describedby": undefined }` se il consumer NON ha
 * passato esplicitamente `aria-describedby` e i children NON contengono un
 * Description. Altrimenti `{}` (lascia che Radix gestisca l'auto-binding).
 */
export function ariaDescribedByOptOut(
  props: { "aria-describedby"?: string },
  children: React.ReactNode,
): { "aria-describedby"?: undefined } {
  const provided =
    "aria-describedby" in props && props["aria-describedby"] !== undefined;
  if (provided) return {};
  return hasDescriptionChild(children) ? {} : { "aria-describedby": undefined };
}
